import React from 'react';
import { Avatar, AvatarGroup, Box, Typography, useTheme } from '@mui/material';
import { cycle } from './TypingIndicator.tsx';
import { TypingUser } from '../../hooks/useTypingPresence';

export function formatTypingLabel(names: string[]): string {
  if (names.length === 0) return '';
  if (names.length === 1) return `${names[0]} is typing…`;
  if (names.length === 2) return `${names[0]} and ${names[1]} are typing…`;
  if (names.length === 3) return `${names[0]}, ${names[1]} and ${names[2]} are typing…`;
  return 'Several people are typing…';
}

interface Props {
  users: TypingUser[];
}

const HumanTypingIndicator: React.FC<Props> = ({ users }) => {
  const theme = useTheme();
  if (users.length === 0) return null;

  const label = formatTypingLabel(users.map((u) => u.displayName));

  return (
    <Box
      role="status"
      aria-live="polite"
      sx={{ display: 'flex', alignItems: 'center', gap: '8px', px: 1.5, pb: 0.5 }}
    >
      <AvatarGroup
        max={3}
        aria-hidden="true"
        sx={{ '& .MuiAvatar-root': { width: 24, height: 24, fontSize: 12 } }}
      >
        {users.slice(0, 3).map((u) => (
          <Avatar key={u.id} src={u.avatarUrl || undefined} alt="">
            {u.displayName?.[0]}
          </Avatar>
        ))}
      </AvatarGroup>
      <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
        {label}
      </Typography>
      <Box aria-hidden="true" sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
        {[0, 0.4, 0.8].map((delay) => (
          <Box
            key={delay}
            sx={{
              width: 6,
              height: 6,
              borderRadius: '50%',
              backgroundColor: theme.palette.text.secondary,
              animation: `${cycle} 1.2s infinite ease-in-out`,
              animationDelay: `${delay}s`,
            }}
          />
        ))}
      </Box>
    </Box>
  );
};

export default HumanTypingIndicator;
